/* eslint-disable no-nested-ternary */
import React, { FC, useCallback, useMemo, useState } from "react";
import classNames from "classnames/dedupe";
import ReactPlayer from "react-player";
import { secondsToMin } from "../../../helpers/formatters";
import useEventListener from "../../../hooks/useEventListener";
import {
  VideoReplayIcon,
  SoundOffIcon,
  SoundOnIcon,
  VideoPauseIcon,
  VideoPlayIcon,
} from "../../../icons";
import { Button } from "../../ActionsSelectors/Button";
import Modal, { IModalProps } from "../../Utility/Modal/Modal";
import ProgressSlider from "../../Utility/MediaBuilder/Video/ProgressSlider/ProgressSlider";
import { useCursorTrack, useVideo } from "../../Utility/MediaBuilder/Video";

const ANIMATION_DURATION = 0.4;
export interface IProductVideoProps extends IModalProps {
  /**
   * Video source URL
   */
  url?: string;
  /**
   * Set false to use native controls
   */
  UDSControls?: boolean;
  /**
   * Set true to use video as thumbnail
   */
  asThumbnail?: boolean;
}

const ProductVideo: FC<IProductVideoProps> = ({
  url,
  UDSControls = true,
  asThumbnail = false,
  ...modal
}) => {
  const [
    videoState,
    player,
    handlePlayPause,
    handleProgress,
    handleDuration,
    handleSeekMouseDown,
    handleSeekMouseUp,
    handleSeekChange,
    toggleMute,
    handleVolumeChange,
  ] = useVideo({
    url,
    playing: true,
    muted: true,
    loop: false,
  });

  /**
   * replay video
   *  */
  const [replay, setReplay] = useState<boolean>(false);

  const handleReplay = useCallback(() => {
    setReplay(!replay);
    handlePlayPause();
  }, [handlePlayPause, replay]);

  /**
   * Hotkey support
   */
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.code === "Space") {
        handlePlayPause();
      }
    },
    [handlePlayPause],
  );

  useEventListener("keydown", handleKeyDown);

  /**
   * User activity tracker, to hide controls
   */
  const [hideControls] = useCursorTrack();

  const hideStyles = useMemo(
    () => ({
      transition: `opacity ${ANIMATION_DURATION}s ease-in`,
      opacity: hideControls ? "0" : "1",
    }),
    [hideControls],
  );

  /**
   * If video is played, render replay control
   * Otherwise display play/pause
   */
  const playIcon = useMemo(() => {
    if (replay) {
      return <VideoReplayIcon />;
    }
    return videoState.playing ? (
      <VideoPauseIcon data-testid="icon-video-pause" />
    ) : (
      <VideoPlayIcon data-testid="icon-video-play" />
    );
  }, [replay, videoState.playing]);

  return (
    <Modal {...modal} exitBtnStyle={hideStyles}>
      <ReactPlayer
        ref={player}
        light={asThumbnail}
        {...videoState}
        width="100%"
        height="100%"
        className="product-video__video"
        onProgress={handleProgress}
        onDuration={handleDuration}
        onEnded={handleReplay}
        playsinline
        data-testid="product-video"
        controls={!UDSControls}
      />

      <Button
        variant="icon"
        classes={{
          root: classNames(
            "position-absolute top-50 start-50 translate-middle btn-animated-fade",
            {
              "uds-fade-out": !replay,
            },
          ),
        }}
        onClick={handleReplay}
      >
        <VideoReplayIcon />
      </Button>

      {UDSControls && (
        <div className="product-video__controls" style={hideStyles}>
          <Button
            classes={{ root: "product-video__button" }}
            onClick={replay ? handleReplay : handlePlayPause}
            data-testid="video-control-btn"
          >
            {playIcon}
          </Button>

          <ProgressSlider
            played={videoState.played}
            onPointerStart={handleSeekMouseDown}
            onChange={handleSeekChange}
            onPointerEnd={handleSeekMouseUp}
          />

          <div className="product-video__time">
            {secondsToMin(videoState.duration * (1 - videoState.played))}
          </div>

          <Button
            classes={{ root: "product-video__button" }}
            onClick={toggleMute}
            data-testid="video-volume-btn"
          >
            {videoState.muted ? (
              <SoundOffIcon data-testid="sound-off" />
            ) : (
              <SoundOnIcon data-testid="sound-on" />
            )}
          </Button>

          <ProgressSlider
            played={videoState.volume}
            onChange={handleVolumeChange}
            classes={{ container: "product-video__sound" }}
          />
        </div>
      )}
    </Modal>
  );
};

export default ProductVideo;
